import type { Prospect, SectorGroup, WebsiteQuality } from '../../types';
import { formatDate, formatRating, googleSearchHref, shortenUrl, telHref } from '../../lib/format';
import { getOpenStatus } from '../../lib/openingHours';
import { accentuate } from '../../lib/accents';
import {
  PRIORITY_LABELS,
  WEBSITE_QUALITY_LABELS,
  openStatusLabel,
  websiteLabel,
  websiteTone,
} from '../lib/labels';
import { countdownLabel, dayLong, dayOf, daysUntil, readTime } from '../lib/rdv';
import { Sheet } from '../components/Sheet';
import { MAX_PROSPECT_SCORE, priorityRingTone, ScoreRing } from '../components/ScoreRing';
import { IconCheck, IconGauge, IconPhone, IconRefresh, IconStar } from '../components/Icons';

export type ProspectAction = 'refresh' | 'analyze' | 'remove' | null;

interface ProspectSheetProps {
  prospect: Prospect;
  onQualityChange: (quality: WebsiteQuality) => void;
  onRefresh: () => void;
  onAnalyze: () => void;
  onRemove: () => void;
  onClose: () => void;
  /** Ouvre la feuille de rendez-vous par-dessus celle-ci. */
  onOpenAppointment: () => void;
  /** Repasse la fiche en « à contacter » et oublie la date. */
  onCancelAppointment: () => void;
  busyAction: ProspectAction;
  isSaving: boolean;
  error: string | null;
  sectorGroups: readonly SectorGroup[];
  now: Date;
}

const VERDICTS: WebsiteQuality[] = ['BAD', 'GOOD'];

/**
 * La fiche prospect, en feuille.
 *
 * ─── Ce qu'elle ne fait plus ───────────────────────────────────────────────
 *
 * Il n'y a plus de bloc « Suivi » : ni menu de statut, ni date de dernier
 * contact, ni sélecteur de métier, ni notes. **Le statut se déduit des gestes**
 * plutôt que de se saisir :
 *
 *   garder depuis les résultats  →  la fiche est dans « Enregistrés »
 *   fixer un rendez-vous         →  elle passe dans « RDV fixés »
 *   annuler le rendez-vous       →  elle retombe dans « Enregistrés »
 *   retirer                      →  elle sort, l'établissement reste traité
 *
 * Un formulaire de suivi suppose qu'on le tienne à jour. Sur un outil qu'on
 * ouvre entre deux appels, il ne l'est jamais — et un statut faux vaut moins
 * que pas de statut du tout. Le métier, lui, vient de la recherche qui a trouvé
 * la fiche : le corriger à la main était une réparation, pas une décision.
 *
 * ─── L'ordre ───────────────────────────────────────────────────────────────
 *
 * Celui d'un appel qu'on passe : ce qui décide si on appelle (score, présence
 * web, note, horaire), puis le numéro et les deux gestes qui suivent l'appel.
 * Ce qui relève de l'entretien de la fiche — verdict sur le site, analyse,
 * actualisation, retrait — est en bas, là où on ne va que délibérément.
 */
export function ProspectSheet(props: ProspectSheetProps) {
  const { prospect } = props;

  const open = getOpenStatus(prospect.openingHours, props.now);
  const score = prospect.savedScore ?? prospect.currentScore;
  const priority = prospect.savedPriority ?? prospect.currentPriority;
  const speed = prospect.pagespeed;

  const hasRdv = prospect.status === 'APPOINTMENT_SET';
  const rdvDay = dayOf(prospect.nextFollowUpAt);
  const rdvTime = readTime(prospect.id);
  const busy = props.busyAction !== null;

  return (
    <Sheet onClose={props.onClose} label={`Fiche de ${prospect.name}`}>
      <div className="sheet-head">
        <div style={{ minWidth: 0 }}>
          <h2 className="sheet-title">{prospect.name}</h2>
          <p className="sheet-meta">
            {prospect.sectorLabel !== null ? `${accentuate(prospect.sectorLabel)} · ` : ''}
            {prospect.address}
          </p>
        </div>

        {/* Un rendez-vous obtenu remplace le score : la question « faut-il
            appeler celui-là ? » ne se pose plus, elle est tranchée. */}
        {hasRdv ? (
          <span className="rdv-check" aria-label="Rendez-vous fixé">
            <IconCheck size={26} />
          </span>
        ) : (
          <ScoreRing
            score={score}
            size={58}
            tone={priorityRingTone(priority)}
            label={`Score ${score} sur ${MAX_PROSPECT_SCORE} — ${PRIORITY_LABELS[priority]}`}
          />
        )}
      </div>

      {props.error !== null ? (
        <p className="notice notice-error" role="alert" style={{ marginTop: 14 }}>
          {props.error}
        </p>
      ) : null}

      {/* --- Le rendez-vous, quand il existe ------------------------------- */}
      {hasRdv ? (
        <div className="rdv-panel sheet-section">
          <span className="eyebrow" style={{ color: '#4a6b5f' }}>
            Rendez-vous fixé
          </span>
          {rdvDay === null ? (
            <p className="rdv-countdown" style={{ marginTop: 6 }}>
              Aucune date enregistrée — fixez-la pour la retrouver dans le bon ordre.
            </p>
          ) : (
            <>
              <p className="rdv-when">
                <span className="rdv-day">{dayLong(rdvDay)}</span>
                {rdvTime !== null ? <span className="rdv-time">{rdvTime}</span> : null}
              </p>
              <p className="rdv-countdown">
                {countdownLabel(daysUntil(rdvDay, props.now))}
                {rdvTime === null ? ' · heure non renseignée' : ''}
              </p>
            </>
          )}
        </div>
      ) : null}

      {/* --- Ce qui décide de l'appel -------------------------------------- */}
      <div className="facts sheet-section">
        <div className="fact">
          <span className="fact-label">Site web</span>
          <span className="fact-value">
            <span
              className={`badge ${websiteTone(prospect.websiteType, prospect.websiteQuality)}`}
            >
              {websiteLabel(prospect.websiteType, prospect.websiteQuality)}
            </span>
          </span>
          {prospect.website !== null ? (
            <a
              href={prospect.website}
              target="_blank"
              rel="noreferrer noopener"
              style={{ display: 'block', marginTop: 6, fontSize: 13 }}
            >
              {shortenUrl(prospect.website)}
            </a>
          ) : null}
        </div>

        <div className="fact">
          <span className="fact-label">Google</span>
          <span className="fact-value">
            {prospect.rating === null ? (
              'Aucun avis'
            ) : (
              <a
                className="rating rating-link"
                style={{ fontSize: 14.5 }}
                href={googleSearchHref(prospect.name, prospect.address)}
                target="_blank"
                rel="noreferrer noopener"
                title="Rechercher sur Google"
              >
                <IconStar size={13} />
                {formatRating(prospect.rating)}
                <span className="faint">· {prospect.userRatingCount ?? 0}</span>
              </a>
            )}
          </span>
          <span
            className={`hours-${open.state.toLowerCase()}`}
            style={{ display: 'block', marginTop: 6, fontSize: 13 }}
          >
            {openStatusLabel(open)}
          </span>
        </div>
      </div>

      {/* --- Le numéro, puis les deux gestes qui suivent l'appel ----------- */}
      <div
        className="card-flat sheet-section"
        style={{ display: 'flex', alignItems: 'center', gap: 11 }}
      >
        <IconPhone size={16} />
        {prospect.phone === null ? (
          <span className="badge badge-danger">Non joignable</span>
        ) : (
          <a href={telHref(prospect.phone)} style={{ fontSize: 15, fontWeight: 600 }}>
            {prospect.phone}
          </a>
        )}
      </div>

      <div className="sheet-actions">
        {prospect.phone !== null ? (
          <a className="btn btn-go" href={telHref(prospect.phone)} style={{ flex: 1.3 }}>
            Appeler
          </a>
        ) : null}
        <button type="button" className="btn btn-soft" onClick={props.onOpenAppointment}>
          {hasRdv ? 'Modifier la date' : 'Fixer un RDV'}
        </button>
      </div>

      {hasRdv ? (
        <button
          type="button"
          className="btn btn-danger btn-block btn-sm"
          style={{ marginTop: 8 }}
          onClick={props.onCancelAppointment}
          disabled={props.isSaving}
        >
          Annuler le rendez-vous
        </button>
      ) : null}

      {/* --- Le site : verdict humain, avis technique à titre indicatif ---- */}
      {prospect.websiteType === 'OWN_WEBSITE' ? (
        <section className="sheet-section sheet-part">
          <span className="eyebrow" style={{ marginBottom: 10 }}>
            Qualité du site
          </span>

          <div className="verdicts">
            {VERDICTS.map((quality) => (
              <button
                key={quality}
                type="button"
                className="chip"
                aria-pressed={prospect.websiteQuality === quality}
                onClick={() => props.onQualityChange(quality)}
              >
                {WEBSITE_QUALITY_LABELS[quality]}
              </button>
            ))}
          </div>

          <p className="cost" style={{ display: 'block', margin: '12px 0 10px' }}>
            {speed !== null
              ? `PageSpeed — perf. ${speed.performance ?? '—'} · access. ${speed.accessibility ?? '—'} · SEO ${speed.seo ?? '—'} · analysé le ${formatDate(speed.analyzedAt)}`
              : 'L’analyse technique informe ton verdict, elle ne le remplace pas : un mauvais score ne vaut pas « mauvais site ».'}
          </p>

          <button
            type="button"
            className="btn btn-soft btn-sm"
            onClick={props.onAnalyze}
            disabled={busy}
          >
            <IconGauge size={15} />
            {props.busyAction === 'analyze'
              ? 'Analyse…'
              : speed !== null
                ? 'Relancer l’analyse'
                : 'Analyser le site'}
          </button>
        </section>
      ) : null}

      {/* --- Entretien de la fiche ----------------------------------------- */}
      <section className="sheet-section sheet-part">
        <span className="eyebrow" style={{ marginBottom: 10 }}>
          La fiche
        </span>
        <p className="sheet-note">
          Gardée le {formatDate(prospect.savedAt)}.
          {prospect.googleMapsUrl !== null ? (
            <>
              {' '}
              <a href={prospect.googleMapsUrl} target="_blank" rel="noreferrer noopener">
                Voir sur Google Maps
              </a>
            </>
          ) : null}
        </p>

        <div className="folder-bar" style={{ marginTop: 12, padding: 0 }}>
          <span className="cost" style={{ flex: 1, minWidth: '12ch' }}>
            Actualiser coûte 1 requête Google.
          </span>
          <button
            type="button"
            className="btn btn-soft btn-sm"
            onClick={props.onRefresh}
            disabled={busy}
          >
            <IconRefresh size={15} />
            {props.busyAction === 'refresh' ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>

        {/* Retirer n'est pas une perte : l'établissement bascule en « déjà
            traité » et reste reconnu lors des prochaines recherches. */}
        <p className="cost" style={{ display: 'block', marginTop: 16 }}>
          Retirer la fiche la sort de la bibliothèque, mais l’établissement reste reconnu comme
          « déjà traité » — la mémoire n’est pas perdue.
        </p>
        <button
          type="button"
          className="btn btn-danger btn-block btn-sm"
          style={{ marginTop: 8 }}
          onClick={props.onRemove}
          disabled={busy}
        >
          {props.busyAction === 'remove' ? 'Retrait…' : 'Retirer de la bibliothèque'}
        </button>
      </section>
    </Sheet>
  );
}
